"use client"
import React, { useState, useEffect } from "react";
import useApi from "@/components/api/useApi";
import { useRouter } from "next/navigation";
import { useMediaQuery } from "react-responsive";
import { Container, Row, Col, Modal } from "react-bootstrap";

import MapPointFormModal from "@/components/maps/MapPointFormModal";
import MapAndPins from "@/components/maps/MapAndPins";

import "@/components/profiles/ProfileLandingPageAnchor.css";
import "@/components/maps/MapView.css";

import CollapsedHeader from "@/components/shared/CollapsedHeader";

import MapPointContent from "@/components/maps/MapPointContent";
import { useEventListener } from "@/lib/events";
import InlineSignupModal from "@/components/login/InlineSignupModal";
import MapViewHero from "@/components/maps/MapViewHero";

var moment = require("moment");

const MapView = ({
  name,
  profile,
  mapId,
  pinId,
  privateAccess,
  utmParams,
  contributePin = false,
  commentsEnabled = false
}) => {
  const router = useRouter();
  const [loaded, setLoaded] = useState(false);
  const [mapView, setMapView] = useState(null);
  const [points, setPoints] = useState(null);
  const [authentication, setAuthentication] = useState(null);
  const [fourOFour, setFourOFour] = useState(false);
  const [submitPoint, setSubmitPoint] = useState(false);
  const [pinToActivate, setPinToActivate] = useState(null);
  const [mapRoutes, setMapRoutes] = useState([]);
  const [fullscreenToggle, setFullscreenToggle] = useState(false);

  const [enableComments, setEnableComments] = useState(false);
  const [registerCommenter, setRegisterCommenter] = useState(false);

  const api = useApi();
  const isSm = useMediaQuery({ maxWidth: 767.98 });

  useEffect(() => {
    var storedAuth = api.getAuthentication();
    if (storedAuth != null) {
      var auth = JSON.parse(storedAuth);
      setAuthentication(auth);
    }
  }, []);

  useEffect(() => {
    setEnableComments(authentication && commentsEnabled);
  }, [authentication, commentsEnabled]);

  useEffect(() => {
    if (!mapView && name && mapId) {
      var mapCall = api.getProfileMap(name, mapId, utmParams);
      if (privateAccess) {
        mapCall = api.getPrivateProfileMap(name, mapId, utmParams);
      }
      mapCall
        .then(mapView => {
          // console.log("MapView api call", mapView);
          setMapView(mapView);
          if (mapView.hasRoutes) {
            api.listExistingMapRoutes(name, mapId).then(theRoutes => {
              setMapRoutes(theRoutes);
            });
          }
          setLoaded(true);
        })
        .catch(error => {
          // console.log(error);
          setFourOFour(true);
        });

      var pointsCall = api.getProfileMapPoints(name, mapId);
      if (privateAccess) {
        pointsCall = api.getPrivateProfileMapPoints(name, mapId, utmParams);
      }
      pointsCall
        .then(points => {
          if (pinId) {
            var pin = points.find(pt => pt.id === pinId);
            if (pin) {
              setPinToActivate(pin);
            }
          }
          setPoints(points);
        })
        .catch(error => {
          setFourOFour(true);
        });
    }
  }, [name, mapId, privateAccess, utmParams]);

  useEffect(() => {
    if (mapView && mapView.allowPublicContributors) {
      if (!authentication && contributePin) {
        setSubmitPoint(true);
      } else if (
        authentication &&
        authentication.profileName !== mapView.owner.resourceName &&
        contributePin
      ) {
        setSubmitPoint(true);
      }
    }
  }, [authentication, mapView, contributePin]);

  const onPointSubmitted = () => {
    setSubmitPoint(false);
    window.flash_messages.alertModal({
      heading: "Pending Approval",
      textContent: (
        <div className="pending-approval-msg">
          <span className="material-icons green">check_circle</span>
          <p>
            Your contribution as been sent to this community’s admin for review.
            If approved, your contribution will appear publicly.
          </p>
        </div>
      )
    });
  };

  const ContributeToMapButton = ({ sm }) => {
    return (
      <>
        {isSm ? (
          <div className="floating-right-buttons">
            <button
              type="button"
              id="create-content"
              className={`new-content-button ${submitPoint ? "hidden" : ""}`}
              onClick={() => setSubmitPoint(true)}
            >
              <i className="material-icons">add</i>
            </button>
          </div>
        ) : (
          <div className="floating-right-buttons">
            <button
              type="button"
              id="create-content"
              className={`new-content-button with-text ${
                submitPoint ? "hidden" : ""
              }`}
              onClick={() => setSubmitPoint(true)}
            >
              <i className="material-icons">add</i>
              <span>Add a Submission</span>
            </button>
          </div>
        )}
      </>
    );
  };

  const turnOnPublicComment = () => {
    if (authentication) {
      setEnableComments(true);
    } else {
      setRegisterCommenter(true);
    }
  };

  useEventListener("add-comment", payload => {
    // make api call, using either your authentication -or- the override token you've provided
    if (mapId === payload.contentId) {
      api
        .submitProfileMapComment(name, mapId, payload.subContentId, {
          content: payload.comment
        })
        .then(response => {
          if (payload.callback) {
            payload.callback(true);
          }
        })
        .catch(error => {
          window.flash_messages.addMessage({
            id: "id",
            text: "We couldn't submit your comment at this time.",
            type: "error"
          });
          if (payload.callback) {
            payload.callback(false);
          }
        });
    }
  });

  const publicCommentsSection = (mapV, isContentOwner) => {
    if (mapV.supportsPublicComments && mapV.allowPublicComments) {
      var endDate = moment(mapV.publicCommentSubmissionEnd);
      if (endDate.isAfter(moment())) {
        var text = "Open to public comment";
        var startDate = moment(mapV.publicCommentSubmissionStart);
        if (startDate.isAfter(moment())) {
          text += ` starting ${startDate.format("MMM DD, yyyy")}`;
        }
        return (
          <Container>
            <Row>
              <Col>
                <div className="public-comment-statement">
                  <h4>
                    <span className="material-icons">forum</span> {text}
                  </h4>
                  <p>{mapView.publicCommentRules}</p>
                  {!isContentOwner && !enableComments && (
                    <>
                      <span>
                        By clicking <b>Turn on comments</b>, you acknowledge the
                        terms of public submission.
                      </span>
                      <br />
                      <br />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => turnOnPublicComment()}
                      >
                        Turn on comments
                      </button>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        );
      } else {
        return <></>;
      }
    } else {
      return <></>;
    }
  };

  useEffect(() => {
    if (fourOFour) router.replace("not-found");
  }, [fourOFour, router]);

  if (fourOFour) {
    return null;
  } else if (mapView == null) {
    return <></>;
  } else {
    var mapUrl = `/${name}/stories/${mapView.id}/edit`;

    const isContentOwner =
      authentication != null &&
      authentication.profileName === mapView.owner.resourceName;

    if (!loaded) {
      return <></>;
    } else {
      return (
        <>
          <InlineSignupModal
            name={name}
            show={registerCommenter}
            type="AMBASSADOR"
            state={`${name}/stories/${mapId}?comment=true`}
            modalTitle="Register to Comment"
            signupHeading="Please create an account."
            signupText="In order to submit your comments, please provide the following information to create your account. We'll send you an activation link to your email address."
            onHide={() => setRegisterCommenter(false)}
          />
          <div className="map-view">
            <MapViewHero
              coverPhotoId={mapView.coverPhotoId}
              title={mapView.title}
              subtitle={mapView.subtitle}
              text={mapView.text}
              location={mapView.location}
            >
              {!isContentOwner && mapView.allowPublicContributors && (
                <ContributeToMapButton />
              )}
              {mapView.cta && (
                <a
                  className="btn btn-secondary with-icon"
                  href={mapView.cta.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {mapView.cta.icon && (
                    <i className={`fas ${mapView.cta.icon}`}></i>
                  )}
                  <span>{mapView.cta.label}</span>
                </a>
              )}
            </MapViewHero>

            <CollapsedHeader
              title={mapView.title}
              imageId={mapView.owner.logoId}
              name={mapView.owner.displayName}
              type={mapView.owner.userProfileType}
              override={fullscreenToggle}
            />

            {publicCommentsSection(mapView, isContentOwner)}

            {pinToActivate && (
              <Modal
                show={true}
                centered
                onHide={() => setPinToActivate(null)}
                scrollable={true}
                fullscreen={true}
              >
                <Modal.Header closeButton>
                  <Modal.Title>{pinToActivate.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <MapPointContent point={pinToActivate} mapId={mapId} />
                </Modal.Body>
              </Modal>
            )}
            <MapAndPins
              name={name}
              mapView={mapView}
              authentication={authentication}
              points={points}
              privateAccess={privateAccess}
              utmParams={utmParams}
              hasHeader={true}
              supportsMediaSubmissions={
                !isContentOwner && mapView.allowPublicContributors
              }
              fullscreenEnabled={true}
              onFullscreenToggle={toggle => {
                setFullscreenToggle(toggle);
                if (toggle) {
                  var mapAndPins = document.getElementById("map-and-pins");
                  if (mapAndPins) {
                    mapAndPins.scrollIntoView();
                  }
                }
              }}
              enableComments={enableComments}
            />

            {submitPoint && (
              <MapPointFormModal
                name={name}
                mapId={mapView.id}
                layers={mapView.layers}
                submission={true}
                modalTitle="Contribute to this Map"
                onPointSubmitted={onPointSubmitted}
                onCancel={() => setSubmitPoint(false)}
                authentication={authentication}
              />
            )}
            <div style={{ marginBottom: "2rem" }} />
          </div>
        </>
      );
    }
  }
};
export default MapView;
